import { Calendar, Mail, RefreshCw, Loader2, Unplug } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useToast } from '@/shared/hooks/useToast';
import { useInstantFormat } from '@/shared/hooks/useInstantFormat';
import {
  useDeleteCalendarConnection,
  useStartManualCalendarSync,
} from '@/features/calendar/public';
import type { CalendarConnection, ConnectionStatus } from '@/features/calendar/public';
import { LinkedCalendarRow } from './LinkedCalendarRow';

interface CalendarConnectionCardProps {
  connection: CalendarConnection;
}

const STATUS_VARIANT: Record<
  ConnectionStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  Active: 'default',
  NeedsReauth: 'secondary',
  RevokedByProvider: 'destructive',
  Disabled: 'outline',
};

export function CalendarConnectionCard({ connection }: CalendarConnectionCardProps) {
  const { t } = useTranslation('calendar');
  const { toast } = useToast();
  const formatInstant = useInstantFormat();
  const deleteConnection = useDeleteCalendarConnection();
  const manualSync = useStartManualCalendarSync();
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();

  const ProviderIcon = connection.provider === 'Google' ? Calendar : Mail;

  async function handleDisconnect() {
    const { confirmed } = await confirm({
      title: t('integrations.connectionCard.disconnectConfirmTitle'),
      description: t('integrations.connectionCard.disconnectConfirmDescription'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    try {
      await deleteConnection.mutateAsync(connection.id);
    } catch {
      // global handler shows error toast
    }
  }

  async function handleSyncNow() {
    try {
      await manualSync.mutateAsync(connection.id);
      toast.success({ title: t('integrations.connectionCard.syncSuccess') });
    } catch {
      // global handler shows error toast
    }
  }

  return (
    <div
      className="rounded-lg border border-border p-4 space-y-4"
      data-testid={`settings-integrations-connection-${connection.id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted">
            <ProviderIcon className="size-4 text-foreground" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground">
              {connection.providerAccountEmail}
            </p>
            <p className="text-xs text-muted-foreground">
              {t(`integrations.providers.${connection.provider}`)}
              {connection.lastSyncedAt &&
                ` · ${t('integrations.connectionCard.lastSynced', {
                  date: formatInstant(connection.lastSyncedAt, { showTime: true }),
                })}`}
            </p>
          </div>
          <Badge
            variant={STATUS_VARIANT[connection.status]}
            data-testid={`settings-integrations-status-${connection.id}`}
          >
            {t(`integrations.status.${connection.status}`)}
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSyncNow}
            disabled={manualSync.isPending || connection.status !== 'Active'}
            data-testid={`settings-integrations-sync-now-button-${connection.id}`}
          >
            {manualSync.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('integrations.connectionCard.syncing')}
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                {t('integrations.connectionCard.syncNow')}
              </>
            )}
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                aria-label={t('integrations.connectionCard.disconnect')}
                onClick={handleDisconnect}
                disabled={deleteConnection.isPending}
                data-testid={`settings-integrations-disconnect-${connection.id}`}
              >
                <Unplug className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('integrations.connectionCard.disconnect')}</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {connection.lastSyncError && (
        <p
          className="text-xs text-destructive"
          data-testid={`settings-integrations-sync-error-${connection.id}`}
        >
          {connection.lastSyncError}
        </p>
      )}

      <div className="space-y-2">
        {connection.calendars.map((calendar) => (
          <LinkedCalendarRow key={calendar.id} calendar={calendar} connectionId={connection.id} />
        ))}
      </div>

      <ConfirmDialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
        title={options?.title ?? ''}
        description={options?.description}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}
